/**
 * Runtime Configuration
 * 
 * Provides access to runtime-injected configuration values.
 * These values are injected by docker-entrypoint.sh at container startup,
 * allowing the same built image to be used across different environments.
 * 
 * For local development, fallback values from VITE_* env vars are used.
 * 
 * @see ADR-004 for parametrizable URLs architecture decision
 */

// =============================================================================
// Types
// =============================================================================

export interface RuntimeConfig {
  /** API base URL for backend calls */
  API_URL: string;
  /** Identity provider URL for auth API calls */
  IDENTITY_PUBLIC_URL: string;
  /** OAuth2 provider URL for OAuth2 flows */
  OAUTH_PUBLIC_URL: string;
  /** WebSocket URL for real-time features */
  WS_URL: string;
  /** Current environment */
  ENVIRONMENT: 'development' | 'staging' | 'production';
}

declare global {
  interface Window {
    __ATLAS_CONFIG__?: Partial<RuntimeConfig>;
  }
}

// =============================================================================
// Default Values (Development)
// =============================================================================

const defaults: RuntimeConfig = {
  API_URL: '/api',
  IDENTITY_PUBLIC_URL: '',
  OAUTH_PUBLIC_URL: '',
  WS_URL: '',
  ENVIRONMENT: 'development',
};

function deriveIdentityPublicUrl(): string {
  if (typeof window === 'undefined') {
    return 'https://auth-eu.local.iovalue.tech';
  }

  const { hostname } = window.location;

  if (hostname.startsWith('auth-')) {
    return `${window.location.protocol}//${hostname}`;
  }

  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const regionCode = parts[0];
    const accountRegion = regionCode.split('-')[0] || 'eu';
    const domain = parts.slice(1).join('.');
    return `${window.location.protocol}//auth-${accountRegion}.${domain}`;
  }

  return 'https://auth-eu.local.iovalue.tech';
}

// =============================================================================
// Configuration Access
// =============================================================================

/**
 * Get runtime configuration with fallback to Vite env vars (for dev)
 * 
 * Priority: window.__ATLAS_CONFIG__ > VITE_* env vars > defaults
 */
function getRuntimeConfig(): RuntimeConfig {
  const runtimeConfig = typeof window !== 'undefined' ? window.__ATLAS_CONFIG__ : undefined;
  const derivedIdentityUrl = deriveIdentityPublicUrl();
  const identityPublicUrl =
    runtimeConfig?.IDENTITY_PUBLIC_URL ||
    import.meta.env.VITE_IDENTITY_URL ||
    defaults.IDENTITY_PUBLIC_URL ||
    derivedIdentityUrl;
  
  return {
    API_URL: runtimeConfig?.API_URL || import.meta.env.VITE_API_URL || defaults.API_URL,
    IDENTITY_PUBLIC_URL: identityPublicUrl,
    OAUTH_PUBLIC_URL:
      runtimeConfig?.OAUTH_PUBLIC_URL ||
      import.meta.env.VITE_OAUTH_URL ||
      defaults.OAUTH_PUBLIC_URL ||
      `${identityPublicUrl}/oauth2`,
    WS_URL: runtimeConfig?.WS_URL || import.meta.env.VITE_WS_URL || defaults.WS_URL,
    ENVIRONMENT: (runtimeConfig?.ENVIRONMENT || import.meta.env.MODE || defaults.ENVIRONMENT) as RuntimeConfig['ENVIRONMENT'],
  };
}

/**
 * Validate required configuration values
 */
export function validateConfig(): void {
  const required: (keyof RuntimeConfig)[] = ['API_URL'];
  for (const key of required) {
    if (!config[key]) {
      console.error(`Missing required config: ${key}`);
    }
  }
}

// Export singleton config
export const config = getRuntimeConfig();

// Re-export individual values for convenience
export const API_URL = config.API_URL;
export const IDENTITY_PUBLIC_URL = config.IDENTITY_PUBLIC_URL;
export const OAUTH_PUBLIC_URL = config.OAUTH_PUBLIC_URL;
export const WS_URL = config.WS_URL;
export const ENVIRONMENT = config.ENVIRONMENT;

// Convenience aliases
export const AUTH_URL = config.IDENTITY_PUBLIC_URL;
export const AUTH_BROWSER_URL = config.IDENTITY_PUBLIC_URL;
